import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MdNotificationsActive, MdHistory, MdClose, MdCalendarToday } from 'react-icons/md';
import api from '../api/axios';

const RemarksTicker = () => {
    const [remarks, setRemarks] = useState([]);
    const [showHistory, setShowHistory] = useState(false);
    const [loading, setLoading] = useState(true);

    const fetchRemarks = async () => {
        try {
            const res = await api.get('/inspections/recent-remarks');
            setRemarks(res.data);
            setLoading(false);
        } catch (error) {
            console.error("Failed to fetch remarks", error);
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRemarks();
        // Poll every 30 seconds for quick updates without reload
        const interval = setInterval(fetchRemarks, 30000);
        return () => clearInterval(interval);
    }, []);

    // If no remarks, don't render anything
    if (!loading && remarks.length === 0) return null;

    // Latest remark is the first one
    const latestRemark = remarks[0];

    return (
        <>
            {/* 1. Slim Ticker Card */}
            <motion.div
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                onClick={() => setShowHistory(true)}
                className="mb-4 bg-gradient-to-r from-red-600/90 to-rose-600/90 backdrop-blur-md rounded-xl shadow-lg border border-white/20 cursor-pointer group hover:shadow-red-500/20 transition-all duration-300 relative overflow-hidden"
            >
                {/* Shine Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />

                <div className="flex items-center justify-between px-4 py-3 relative z-10 w-full">
                    <div className="flex items-center gap-3 overflow-hidden flex-1">
                        {/* Pulse Icon */}
                        <div className="relative shrink-0">
                            <div className="absolute inset-0 bg-white rounded-full animate-ping opacity-75"></div>
                            <div className="relative bg-white text-red-600 rounded-full p-1.5 shadow-sm">
                                <MdNotificationsActive size={16} />
                            </div>
                        </div>

                        {/* Text Content */}
                        <div className="flex flex-col min-w-0 flex-1">
                            <div className="flex items-center gap-2 mb-0.5">
                                <span className="text-[10px] font-extrabold text-white uppercase tracking-wider bg-black/20 px-1.5 py-0.5 rounded shadow-sm">
                                    ADMIN REMARK
                                </span>
                                <span className="text-[10px] text-white/90 font-semibold flex items-center gap-1 opacity-90">
                                    <MdCalendarToday size={10} />
                                    {new Date(latestRemark?.report_date).toLocaleDateString()}
                                </span>
                            </div>
                            <p className="text-sm font-bold text-white truncate w-full pr-2 leading-tight drop-shadow-sm">
                                {latestRemark?.admin_remarks}
                            </p>
                        </div>
                    </div>

                    {/* Action Hint - Mobile optimized */}
                    <div className="flex items-center gap-1 text-[10px] font-bold text-white/90 bg-white/10 px-3 py-1.5 rounded-full group-hover:bg-white/20 transition-colors shrink-0 ml-2 shadow-sm border border-white/10">
                        <span className="hidden sm:inline">View History</span>
                        <MdHistory size={14} />
                    </div>
                </div>
            </motion.div>

            {/* 2. History Modal */}
            <AnimatePresence>
                {showHistory && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
                        onClick={() => setShowHistory(false)} // Close on background click
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.9, opacity: 0, y: 20 }}
                            onClick={(e) => e.stopPropagation()} // Prevent close on modal click
                            className="bg-[#1a1f2e] w-full max-w-md rounded-xl border border-white/10 shadow-2xl overflow-hidden"
                        >
                            {/* Header */}
                            <div className="px-5 py-4 border-b border-white/10 flex justify-between items-center bg-[#151925]">
                                <div className="flex items-center gap-2 text-white">
                                    <div className="bg-red-500/20 p-1.5 rounded-lg text-red-400">
                                        <MdHistory size={20} />
                                    </div>
                                    <div>
                                        <h3 className="text-base font-bold leading-none">Remark History</h3>
                                        <p className="text-[10px] text-gray-400 mt-1">Last 3 Active Remarks</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setShowHistory(false)}
                                    className="text-gray-400 hover:text-white p-1 hover:bg-white/10 rounded transition-colors"
                                >
                                    <MdClose size={20} />
                                </button>
                            </div>

                            {/* List */}
                            <div className="max-h-[60vh] overflow-y-auto p-4 space-y-3">
                                {remarks.map((item, index) => (
                                    <div
                                        key={item.id}
                                        className={`p-3 rounded-lg border ${index === 0
                                            ? 'bg-gradient-to-br from-red-500/10 to-orange-500/5 border-red-500/30'
                                            : 'bg-white/5 border-white/5'
                                            }`}
                                    >
                                        <div className="flex justify-between items-start mb-2">
                                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 ${index === 0 ? 'bg-red-500 text-white' : 'bg-gray-700 text-gray-300'
                                                }`}>
                                                <MdCalendarToday size={10} />
                                                Report: {new Date(item.report_date).toLocaleDateString()}
                                            </span>
                                            {index === 0 && (
                                                <span className="text-[10px] font-bold text-red-400 animate-pulse">
                                                    LATEST
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-sm text-gray-200 leading-relaxed font-medium">
                                            {item.admin_remarks}
                                        </p>
                                        <div className="mt-2 text-[10px] text-gray-500 text-right">
                                            Sent: {new Date(item.created_at).toLocaleString()}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Footer */}
                            <div className="p-3 bg-[#151925] border-t border-white/10 text-center">
                                <p className="text-[10px] text-gray-500">
                                    Only the last 3 remarks are shown. Older remarks are auto-archived.
                                </p>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default RemarksTicker;
